import React from 'react';

interface ModalProps {
  open: boolean;
  onClose?: () => void;
  onOk?: () => void;
  onCancel?: () => void;
  title?: string;
  children: React.ReactNode;
  okText?: string;
  cancelText?: string;
  okDisabled?: boolean;
  containerClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  closeButtonClassName?: string;
}

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  onOk,
  onCancel,
  title,
  children,
  okText = 'Save',
  cancelText = 'Cancel',
  okDisabled = false,
  containerClassName = '',
  headerClassName = '',
  titleClassName = '',
  contentClassName = '',
  footerClassName = '',
  closeButtonClassName = '',
}) => {
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        const closeHandler = onCancel || onClose;
        if (closeHandler) {
          closeHandler();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, onCancel]);

  if (!open) return null;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const handleOk = () => {
    if (onOk) {
      onOk();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`relative w-full max-w-lg max-h-[90vh] flex flex-col bg-white rounded-lg shadow-lg overflow-hidden ${containerClassName}`}
      >
        {(title || onClose || onCancel) && (
          <div
            className={`flex items-center justify-between px-6 py-4 border-b border-gray-200 ${headerClassName}`}
          >
            {title && (
              <h3 className={`text-lg font-semibold text-gray-900 ${titleClassName}`}>
                {title}
              </h3>
            )}
            <button
              className={`text-gray-400 hover:text-gray-600 focus:outline-none ml-auto ${closeButtonClassName}`}
              aria-label="Close"
              onClick={handleCancel}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        <div className={`overflow-y-auto p-6 flex-1 ${contentClassName}`}>
          {children}
        </div>

        {(onOk || onCancel || onClose) && (
          <div
            className={`flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 ${footerClassName}`}
          >
            {onOk && (
              <button
                type="button"
                disabled={okDisabled}
                onClick={handleOk}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-cyan-600"
              >
                {okText}
              </button>
            )}

            {(onCancel || onClose) && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                {cancelText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;